/**
 * New Field Sheet
 * Sheet/drawer for creating a new field with two steps:
 * 1. Select interface type (grouped by category)
 * 2. Configure field details (interface-specific options)
 * 
 * Following reference implementation pattern where each interface
 * defines its own supported types and options.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Check, Loader2, Star, Type, Hash, Calendar, Link2, Image, FileText, List, LayoutGrid, MousePointer, Radio, CheckSquare, Plus, Trash2, ExternalLink, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCreateField, useFieldsForCollectionQuery } from '@/hooks/useFieldsQuery';
import { useCollections, useCreateCollection } from '@/hooks/useCollections';
import { useCreateRelation } from '@/hooks/useRelations';
import { toast } from 'sonner';
import { FolderSelect } from '@/components/FolderSelect';

interface NewFieldSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    collection: string;
    onSuccess?: () => void;
}

// Interface type definition matching reference pattern
interface FieldInterfaceConfig {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
    group: 'standard' | 'selection' | 'relational' | 'presentation' | 'group';
    comingSoon?: boolean;
    // Types supported by this interface (like reference)
    types: string[];
    // Interface-specific options schema
    optionsSchema?: {
        // Common
        placeholder?: boolean;
        icon?: boolean;  // For dropdown icon
        iconLeft?: boolean;
        iconRight?: boolean;
        // Selection
        choices?: boolean;
        allowOther?: boolean;
        allowNone?: boolean;
        // M2O relational options
        relatedCollection?: boolean;
        displayTemplate?: boolean;
        enableCreate?: boolean;
        enableSelect?: boolean;
        enableLink?: boolean;
        // Markdown options
        toolbar?: boolean;
        // DateTime options
        format?: boolean;
        includeSeconds?: boolean;
        use24?: boolean;
        // Button Links options
        links?: boolean;
    };
}

// Interface definitions with proper type constraints (matching reference)
const FIELD_INTERFACES: FieldInterfaceConfig[] = [
    // Text & Numbers - Input interface supports these types (reference: input/index.ts)
    {
        id: 'input',
        label: 'Input',
        icon: Type,
        description: 'Single line text input',
        group: 'standard',
        types: ['string', 'uuid', 'bigInteger', 'integer', 'float', 'decimal', 'text'],
        optionsSchema: {
            placeholder: true,
            iconLeft: true,
            iconRight: true
        }
    },
    {
        id: 'input-url',
        label: 'URL',
        icon: Link2,
        description: 'URL input with validation and copy button',
        group: 'standard',
        types: ['text', 'string'],
        optionsSchema: {
            placeholder: true,
            iconLeft: true,
            iconRight: true
        }
    },
    {
        id: 'input-multiline',
        label: 'Textarea',
        icon: FileText,
        description: 'Multi-line text area',
        group: 'standard',
        types: ['text', 'string'],
        optionsSchema: { placeholder: true }
    },
    {
        id: 'input-rich-text-md',
        label: 'Markdown',
        icon: FileText,
        description: 'Markdown editor',
        group: 'standard',
        types: ['text'],
        optionsSchema: {
            placeholder: true,
            // Markdown specific options
            toolbar: true
        }
    },
    {
        id: 'input-code',
        label: 'Code',
        icon: Type,
        description: 'Code editor with syntax highlighting',
        group: 'standard',
        types: ['text', 'string'],
        comingSoon: true
    },

    // Selection interfaces (reference: select-dropdown/index.ts)
    {
        id: 'select-dropdown',
        label: 'Dropdown',
        icon: List,
        description: 'Single selection dropdown',
        group: 'selection',
        types: ['string', 'integer', 'float', 'decimal', 'bigInteger'],
        optionsSchema: {
            choices: true,
            allowOther: true,
            allowNone: true,
            icon: true,
            placeholder: true
        }
    },
    {
        id: 'select-multiple-dropdown',
        label: 'Dropdown (Multiple)',
        icon: List,
        description: 'Multi-selection dropdown with choices',
        group: 'selection',
        types: ['json', 'csv'],
        optionsSchema: { choices: true, allowOther: true, placeholder: true }
    },
    {
        id: 'boolean',
        label: 'Checkbox',
        icon: CheckSquare,
        description: 'Boolean toggle/checkbox',
        group: 'selection',
        types: ['boolean']
    },
    {
        id: 'datetime',
        label: 'DateTime',
        icon: Calendar,
        description: 'Date and time picker',
        group: 'selection',
        types: ['timestamp', 'datetime', 'date', 'time'],
        optionsSchema: {
            // DateTime specific options
            format: true,
            includeSeconds: true,
            use24: true
        }
    },
    {
        id: 'select-radio',
        label: 'Radio Buttons',
        icon: Radio,
        description: 'Single selection radio buttons',
        group: 'selection',
        types: ['string', 'integer'],
        optionsSchema: { choices: true }
    },
    {
        id: 'select-color',
        label: 'Color',
        icon: Hash,
        description: 'Color picker',
        group: 'selection',
        types: ['string'],
        comingSoon: true
    },
    {
        id: 'select-icon',
        label: 'Icon',
        icon: LayoutGrid,
        description: 'Icon selector',
        group: 'selection',
        types: ['string'],
        comingSoon: true
    },

    // Relational - Only M2O enabled for now (reference: select-dropdown-m2o)
    {
        id: 'select-dropdown-m2o',
        label: 'Many to One',
        icon: Link2,
        description: 'Many items related to one item',
        group: 'relational',
        types: ['uuid', 'integer', 'string', 'bigInteger'],
        optionsSchema: {
            relatedCollection: true,
            displayTemplate: true,
            enableCreate: true,
            enableSelect: true,
            enableLink: true
        }
    },
    {
        id: 'list-m2m',
        label: 'Many to Many',
        icon: Link2,
        description: 'Bidirectional relation between collections',
        group: 'relational',
        types: ['alias'],
        optionsSchema: {
            relatedCollection: true,
            displayTemplate: true,
            enableCreate: true,
            enableSelect: true,
        }
    },
    {
        id: 'list-o2m',
        label: 'One to Many',
        icon: Link2,
        description: 'One item related to many items',
        group: 'relational',
        types: ['alias'],
        comingSoon: true
    },
    {
        id: 'file',
        label: 'File',
        icon: FileText,
        description: 'Single file upload',
        group: 'relational',
        types: ['uuid'],
        optionsSchema: {
            enableCreate: true,
            enableSelect: true
        }
    },
    {
        id: 'file-image',
        label: 'Image',
        icon: Image,
        description: 'Single image upload',
        group: 'relational',
        types: ['uuid'],
        optionsSchema: {
            enableCreate: true,
            enableSelect: true
        }
    },

    // Presentation - Enable button links (reference: presentation-links/index.ts)
    {
        id: 'presentation-links',
        label: 'Button Links',
        icon: MousePointer,
        description: 'Clickable button links',
        group: 'presentation',
        types: ['alias'],
        optionsSchema: {
            // Button links specific
            links: true
        }
    },
    {
        id: 'presentation-divider',
        label: 'Divider',
        icon: LayoutGrid,
        description: 'Visual divider line',
        group: 'presentation',
        types: ['alias'],
        comingSoon: true
    },

    // Groups
    {
        id: 'group-accordion',
        label: 'Accordion',
        icon: LayoutGrid,
        description: 'Collapsible field group',
        group: 'group',
        types: ['alias'],
        comingSoon: true
    },
    {
        id: 'group-detail',
        label: 'Detail Group',
        icon: LayoutGrid,
        description: 'Detail section group',
        group: 'group',
        types: ['alias'],
        comingSoon: true
    },
];

const INTERFACE_GROUPS = [
    { key: 'standard', name: 'Text & Numbers' },
    { key: 'selection', name: 'Selection' },
    { key: 'relational', name: 'Relational' },
    { key: 'presentation', name: 'Presentation' },
    { key: 'group', name: 'Groups' },
] as const;

// Choice item for dropdown/radio
interface ChoiceItem {
    text: string;
    value: string;
}

// Link item for presentation-links
interface LinkItem {
    label: string;
    icon: string;
    type: 'primary' | 'normal' | 'info' | 'success' | 'warning' | 'danger';
    url: string;
}

// Markdown toolbar options
const MARKDOWN_TOOLBAR_OPTIONS = [
    { value: 'heading', label: 'Heading' },
    { value: 'bold', label: 'Bold' },
    { value: 'italic', label: 'Italic' },
    { value: 'strikethrough', label: 'Strikethrough' },
    { value: 'bullist', label: 'Bullet List' },
    { value: 'numlist', label: 'Numbered List' },
    { value: 'blockquote', label: 'Blockquote' },
    { value: 'code', label: 'Code' },
    { value: 'link', label: 'Link' },
    { value: 'table', label: 'Table' },
    { value: 'image', label: 'Image' },
] as const;

// Button type options for presentation-links
const BUTTON_TYPE_OPTIONS = [
    { value: 'primary', label: 'Primary' },
    { value: 'normal', label: 'Normal' },
    { value: 'info', label: 'Info' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'danger', label: 'Danger' },
] as const;

export function NewFieldSheet({ open, onOpenChange, collection, onSuccess }: NewFieldSheetProps) {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selectedInterface, setSelectedInterface] = useState<FieldInterfaceConfig | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Field configuration state
    const [fieldKey, setFieldKey] = useState('');
    const [fieldType, setFieldType] = useState('');
    const [defaultValue, setDefaultValue] = useState('');
    const [isRequired, setIsRequired] = useState(false);
    const [displayName, setDisplayName] = useState('');

    // Interface options state
    const [placeholder, setPlaceholder] = useState('');
    const [icon, setIcon] = useState('');  // For dropdown icon
    const [iconLeft, setIconLeft] = useState('');
    const [iconRight, setIconRight] = useState('');
    const [choices, setChoices] = useState<ChoiceItem[]>([{ text: '', value: '' }]);
    const [allowOther, setAllowOther] = useState(false);
    const [allowNone, setAllowNone] = useState(false);

    // M2O relational options
    const [relatedCollection, setRelatedCollection] = useState('');
    const [displayTemplate, setDisplayTemplate] = useState('');
    const [enableCreate, setEnableCreate] = useState(true);
    const [enableSelect, setEnableSelect] = useState(true);
    const [enableLink, setEnableLink] = useState(false);
    const [defaultFolder, setDefaultFolder] = useState<string | null>(null);

    // Markdown options
    const [toolbarOptions, setToolbarOptions] = useState<string[]>(['heading', 'bold', 'italic', 'strikethrough', 'bullist', 'numlist', 'blockquote', 'code', 'link', 'table', 'image']);

    // DateTime options
    const [dateTimeFormat, setDateTimeFormat] = useState('long');

    // Button Links options
    const [links, setLinks] = useState<LinkItem[]>([{ label: '', icon: '', type: 'normal', url: '' }]);

    // M2M options
    const [junctionCollection, setJunctionCollection] = useState('');
    const [autoGenerateJunction, setAutoGenerateJunction] = useState(true);
    const [_sortField, setSortField] = useState('');
    const [correspondingField, setCorrespondingField] = useState('');

    const createField = useCreateField(collection);
    const createRelation = useCreateRelation();
    const createCollection = useCreateCollection();

    // Get all collections for M2O related collection selection
    const { data: collections = [] } = useCollections();

    // Fetch fields of the selected related collection to detect PK type
    const { data: relatedFields = [] } = useFieldsForCollectionQuery(relatedCollection);

    // Find the primary key field of the related collection and get its type
    const relatedPrimaryKey = relatedFields.find(f => f.schema?.is_primary_key);
    const relatedPkType = relatedPrimaryKey?.type;

    // Auto-update field type when related collection changes for M2O
    const handleRelatedCollectionChange = (value: string) => {
        setRelatedCollection(value);
    };

    // When related collection's PK type is detected, update field type if M2O
    useEffect(() => {
        if (selectedInterface?.id === 'select-dropdown-m2o' && relatedPkType) {
            // Map the PK type to a type supported by M2O interface
            const m2oTypes = ['uuid', 'integer', 'string', 'bigInteger'];
            if (m2oTypes.includes(relatedPkType)) {
                setFieldType(relatedPkType);
            } else if (relatedPkType === 'bigint' || relatedPkType === 'int8') {
                setFieldType('bigInteger');
            } else if (relatedPkType === 'int' || relatedPkType === 'int4' || relatedPkType === 'smallint') {
                setFieldType('integer');
            } else if (relatedPkType === 'text' || relatedPkType === 'varchar' || relatedPkType === 'char') {
                setFieldType('string');
            }
        }
    }, [relatedPkType, selectedInterface]);

    useEffect(() => {
        if (selectedInterface?.id !== 'file' && selectedInterface?.id !== 'file-image') {
            setDefaultFolder(null);
        }
    }, [selectedInterface]);

    // Auto-generate junction collection name for M2M
    useEffect(() => {
        if (selectedInterface?.id === 'list-m2m' && autoGenerateJunction && relatedCollection) {
            // Generate junction collection name like: collection_relatedCollection
            const sorted = [collection, relatedCollection].sort();
            const autoName = `${sorted[0]}_${sorted[1]}`;
            setJunctionCollection(autoName);
        }
    }, [selectedInterface, autoGenerateJunction, collection, relatedCollection]);

    const resetForm = () => {
        setStep('select');
        setSelectedInterface(null);
        setSearchQuery('');
        setFieldKey('');
        setFieldType('');
        setDefaultValue('');
        setIsRequired(false);
        setDisplayName('');
        setPlaceholder('');
        setIcon('');
        setIconLeft('');
        setIconRight('');
        setChoices([{ text: '', value: '' }]);
        setAllowOther(false);
        setAllowNone(false);
        // Reset M2O options
        setRelatedCollection('');
        setDisplayTemplate('');
        setEnableCreate(true);
        setEnableSelect(true);
        setEnableLink(false);
        setDefaultFolder(null);
        // Reset Markdown options
        setToolbarOptions(['heading', 'bold', 'italic', 'strikethrough', 'bullist', 'numlist', 'blockquote', 'code', 'link', 'table', 'image']);
        // Reset DateTime options
        setDateTimeFormat('long');
        // Reset Button Links options
        setLinks([{ label: '', icon: '', type: 'normal', url: '' }]);
        // Reset M2M options
        setJunctionCollection('');
        setAutoGenerateJunction(true);
        setSortField('');
        setCorrespondingField('');
    };

    const handleClose = () => {
        resetForm();
        onOpenChange(false);
    };

    const handleSelectInterface = (iface: FieldInterfaceConfig) => {
        if (iface.comingSoon) {
            toast.info(`${iface.label} is coming soon!`);
            return;
        }
        setSelectedInterface(iface);
        // Set default type to first available type for this interface
        setFieldType(iface.types[0] || 'string');
        setStep('configure');
    };

    const handleBack = () => {
        setStep('select');
    };

    const isValidKey = fieldKey.trim().length > 0 && /^[a-z_][a-z0-9_]*$/i.test(fieldKey);

    // Get type options for the selected interface
    const typeOptions = selectedInterface?.types.map(type => ({
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1)
    })) || [];

    // Add choice row
    const addChoice = () => {
        setChoices([...choices, { text: '', value: '' }]);
    };

    // Remove choice row
    const removeChoice = (index: number) => {
        setChoices(choices.filter((_, i) => i !== index));
    };

    // Update choice
    const updateChoice = (index: number, field: 'text' | 'value', newValue: string) => {
        const newChoices = [...choices];
        const current = newChoices[index];
        if (current) {
            newChoices[index] = {
                text: field === 'text' ? newValue : current.text,
                value: field === 'value' ? newValue : current.value
            };
            setChoices(newChoices);
        }
    };

    // Export choices to CSV
    const exportChoicesToCSV = () => {
        if (choices.length === 0) {
            toast.error('No choices to export');
            return;
        }

        // Filter out empty choices
        const validChoices = choices.filter(c => c.text.trim() || c.value.trim());
        if (validChoices.length === 0) {
            toast.error('No valid choices to export');
            return;
        }

        // Create CSV content
        const csvContent = 'text,value\n' + validChoices.map(c =>
            `"${c.text.replace(/"/g, '""')}","${c.value.replace(/"/g, '""')}"`
        ).join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${fieldKey || 'field'}_choices.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Choices exported to CSV');
    };

    // Import choices from CSV
    const importChoicesFromCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Reset the input so the same file can be selected again
        event.target.value = '';

        if (!file.name.endsWith('.csv')) {
            toast.error('Please select a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').map(line => line.trim()).filter(line => line);

                if (lines.length < 2) {
                    toast.error('CSV file must contain at least a header and one data row');
                    return;
                }

                // Parse CSV (simple parser for text,value format)
                const parseCSVLine = (line: string): string[] => {
                    const result: string[] = [];
                    let current = '';
                    let inQuotes = false;

                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        const nextChar = line[i + 1];

                        if (char === '"') {
                            if (inQuotes && nextChar === '"') {
                                current += '"';
                                i++; // Skip next quote
                            } else {
                                inQuotes = !inQuotes;
                            }
                        } else if (char === ',' && !inQuotes) {
                            result.push(current);
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    result.push(current);
                    return result;
                };

                // Skip header
                const dataLines = lines.slice(1);
                const importedChoices: ChoiceItem[] = [];

                for (const line of dataLines) {
                    const [text, value] = parseCSVLine(line);
                    if (text !== undefined && value !== undefined) {
                        importedChoices.push({
                            text: text.trim(),
                            value: value.trim()
                        });
                    }
                }

                if (importedChoices.length === 0) {
                    toast.error('No valid choices found in CSV file');
                    return;
                }

                setChoices(importedChoices);
                toast.success(`Imported ${importedChoices.length} choice${importedChoices.length !== 1 ? 's' : ''}`);
            } catch (error) {
                console.error('CSV import error:', error);
                toast.error('Failed to parse CSV file');
            }
        };

        reader.onerror = () => {
            toast.error('Failed to read CSV file');
        };

        reader.readAsText(file);
    };

    // Add link row (for button links)
    const addLink = () => {
        setLinks([...links, { label: '', icon: '', type: 'normal', url: '' }]);
    };

    // Remove link row
    const removeLink = (index: number) => {
        setLinks(links.filter((_, i) => i !== index));
    };

    // Update link
    const updateLink = (index: number, field: keyof LinkItem, newValue: string) => {
        const newLinks = [...links];
        const current = newLinks[index];
        if (current) {
            newLinks[index] = { ...current, [field]: newValue };
            setLinks(newLinks);
        }
    };

    // Toggle toolbar option for markdown
    const toggleToolbarOption = (option: string) => {
        if (toolbarOptions.includes(option)) {
            setToolbarOptions(toolbarOptions.filter(o => o !== option));
        } else {
            setToolbarOptions([...toolbarOptions, option]);
        }
    };

    const handleCreate = () => {
        if (!selectedInterface || !isValidKey) return;

        const isAlias = fieldType === 'alias';

        // Build interface options based on what was collected
        const interfaceOptions: Record<string, unknown> = {};
        if (selectedInterface.optionsSchema?.placeholder && placeholder) {
            interfaceOptions.placeholder = placeholder;
        }
        if (selectedInterface.optionsSchema?.icon && icon) {
            interfaceOptions.icon = icon;
        }
        if (selectedInterface.optionsSchema?.iconLeft && iconLeft) {
            interfaceOptions.iconLeft = iconLeft;
        }
        if (selectedInterface.optionsSchema?.iconRight && iconRight) {
            interfaceOptions.iconRight = iconRight;
        }
        if (selectedInterface.optionsSchema?.choices) {
            const validChoices = choices.filter(c => c.text.trim() && c.value.trim());
            if (validChoices.length > 0) {
                interfaceOptions.choices = validChoices;
            }
        }
        if (selectedInterface.optionsSchema?.allowOther) {
            interfaceOptions.allowOther = allowOther;
        }
        if (selectedInterface.optionsSchema?.allowNone) {
            interfaceOptions.allowNone = allowNone;
        }

        // Markdown options
        if (selectedInterface.id === 'input-rich-text-md') {
            interfaceOptions.toolbar = toolbarOptions;
        }

        // DateTime options
        if (selectedInterface.id === 'datetime') {
            if (dateTimeFormat) {
                interfaceOptions.format = dateTimeFormat;
            }
        }

        // Button Links options
        if (selectedInterface.id === 'presentation-links') {
            const validLinks = links.filter(l => l.label.trim());
            if (validLinks.length > 0) {
                interfaceOptions.links = validLinks;
            }
        }

        // M2O relational options
        if (selectedInterface.id === 'select-dropdown-m2o') {
            if (displayTemplate) {
                interfaceOptions.template = displayTemplate;
            }
            interfaceOptions.enableCreate = enableCreate;
            interfaceOptions.enableSelect = enableSelect;
            interfaceOptions.enableLink = enableLink;
        }

        // File/Image options
        if (selectedInterface.id === 'file' || selectedInterface.id === 'file-image') {
            interfaceOptions.enableCreate = enableCreate;
            interfaceOptions.enableSelect = enableSelect;
            if (defaultFolder !== null) {
                interfaceOptions.folder = defaultFolder;
            }
        }

        // M2M options
        if (selectedInterface.id === 'list-m2m') {
            if (displayTemplate) {
                interfaceOptions.template = displayTemplate;
            }
            interfaceOptions.enableCreate = enableCreate;
            interfaceOptions.enableSelect = enableSelect;
            interfaceOptions.layout = 'list';
        }

        // Check if this is M2O - need to create relation separately
        const isM2O = selectedInterface.id === 'select-dropdown-m2o';

        // Check if this is M2M - need to create junction collection and relations
        const isM2M = selectedInterface.id === 'list-m2m';

        // Check if this is File/Image - need to create relation to cms_files
        const isFile = selectedInterface.id === 'file' || selectedInterface.id === 'file-image';

        // Check if this is a presentation field (alias type)
        const isPresentation = selectedInterface.id === 'presentation-links' || selectedInterface.id === 'presentation-divider';

        // Determine the special array based on interface type
        const getSpecial = (): string[] | undefined => {
            if (isM2O) return ['m2o'];
            if (isM2M) return ['m2m'];
            if (isFile) return ['file'];
            if (isPresentation) return ['alias'];  // Presentation fields are alias types
            if (isAlias) return ['alias'];  // Any other alias type field
            return undefined;
        };

        // Build field payload (without relation - that's created separately)
        const payload = {
            field: fieldKey,
            type: fieldType,
            meta: {
                interface: selectedInterface.id,
                options: Object.keys(interfaceOptions).length > 0 ? interfaceOptions : undefined,
                required: isRequired,
                special: getSpecial(),
                // Add translations if display name is provided
                translations: displayName.trim()
                    ? [{ language: 'en-US', translation: displayName.trim() }]
                    : undefined,
            },
            schema: isAlias
                ? null
                : {
                    is_nullable: !isRequired,
                    default_value: defaultValue || undefined,
                },
        };

        createField.mutate(payload as Parameters<typeof createField.mutate>[0], {
            onSuccess: async () => {
                // If M2O, create the relation after field is created
                if (isM2O && relatedCollection) {
                    try {
                        await createRelation.mutateAsync({
                            collection: collection,
                            field: fieldKey,
                            related_collection: relatedCollection,
                            schema: {
                                on_delete: 'SET NULL',
                            },
                            meta: {
                                many_collection: collection,
                                many_field: fieldKey,
                                one_collection: relatedCollection,
                                one_field: null,
                                one_collection_field: null,
                                one_allowed_collections: null,
                                junction_field: null,
                                sort_field: null,
                                one_deselect_action: 'nullify',
                            },
                        });
                    } catch (relationError) {
                        // Field was created but relation failed - warn user
                        toast.warning(`Field created but relation setup failed. You may need to configure the relation manually.`);
                        console.error('Failed to create relation:', relationError);
                    }
                }

                // If File/Image, create relation to cms_files
                if (isFile) {
                    try {
                        await createRelation.mutateAsync({
                            collection: collection,
                            field: fieldKey,
                            related_collection: 'cms_files',
                            schema: {
                                on_delete: 'SET NULL',
                            },
                            meta: {
                                many_collection: collection,
                                many_field: fieldKey,
                                one_collection: 'cms_files',
                                one_field: null,
                                one_collection_field: null,
                                one_allowed_collections: null,
                                junction_field: null,
                                sort_field: null,
                                one_deselect_action: 'nullify',
                            },
                        });
                    } catch (relationError) {
                        toast.warning(`Field created but file relation setup failed. You may need to configure the relation manually.`);
                        console.error('Failed to create file relation:', relationError);
                    }
                }

                // If M2M, create junction collection and relations
                if (isM2M && relatedCollection && junctionCollection) {
                    try {
                        // Get primary key field name for both collections
                        const currentPkField = 'id'; // Standard PK
                        const relatedPkField = relatedFields.find(f => f.schema?.is_primary_key)?.field || 'id';

                        // Junction field names
                        const junctionFieldCurrent = `${collection}_${currentPkField}`;
                        const junctionFieldRelated = `${relatedCollection}_${relatedPkField}`;

                        // Step 1: Create junction collection with fields
                        const junctionExists = collections.some(c => c.collection === junctionCollection);

                        if (!junctionExists) {
                            await createCollection.mutateAsync({
                                collection: junctionCollection,
                                meta: {
                                    hidden: true,
                                    icon: 'import_export',
                                },
                                fields: [
                                    {
                                        field: 'id',
                                        type: 'integer',
                                        schema: {
                                            has_auto_increment: true,
                                            is_primary_key: true,
                                        },
                                        meta: {
                                            hidden: true,
                                        },
                                    },
                                    {
                                        field: junctionFieldCurrent,
                                        type: 'integer',
                                        schema: {},
                                        meta: {
                                            hidden: true,
                                        },
                                    },
                                    {
                                        field: junctionFieldRelated,
                                        type: 'integer',
                                        schema: {},
                                        meta: {
                                            hidden: true,
                                        },
                                    },
                                ],
                            });
                        }

                        // Step 2: Create O2M relation (current collection -> junction)
                        await createRelation.mutateAsync({
                            collection: junctionCollection,
                            field: junctionFieldCurrent,
                            related_collection: collection,
                            schema: {
                                on_delete: 'SET NULL',
                            },
                            meta: {
                                many_collection: junctionCollection,
                                many_field: junctionFieldCurrent,
                                one_collection: collection,
                                one_field: fieldKey,
                                junction_field: junctionFieldRelated,
                                sort_field: null,
                                one_deselect_action: 'nullify',
                            },
                        });

                        // Step 3: Create M2O relation (junction -> related collection)
                        await createRelation.mutateAsync({
                            collection: junctionCollection,
                            field: junctionFieldRelated,
                            related_collection: relatedCollection,
                            schema: {
                                on_delete: 'SET NULL',
                            },
                            meta: {
                                many_collection: junctionCollection,
                                many_field: junctionFieldRelated,
                                one_collection: relatedCollection,
                                one_field: correspondingField || null,
                                junction_field: junctionFieldCurrent,
                                sort_field: null,
                                one_deselect_action: 'nullify',
                            },
                        });

                    } catch (m2mError) {
                        toast.warning(`Field created but M2M relationship setup failed. You may need to configure the relations manually.`);
                        console.error('Failed to create M2M relations:', m2mError);
                    }
                }

                toast.success('Field created successfully');
                handleClose();
                onSuccess?.();
            },
            onError: (error: Error) => {
                toast.error(`Failed to create field: ${error.message}`);
            },
        });
    };

    // Filter interfaces by search query
    const filteredGroups = INTERFACE_GROUPS.map(group => ({
        ...group,
        interfaces: FIELD_INTERFACES.filter(iface =>
            iface.group === group.key &&
            (searchQuery === '' ||
                iface.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                iface.description.toLowerCase().includes(searchQuery.toLowerCase()))
        )
    })).filter(group => group.interfaces.length > 0);

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent className="w-[700px] sm:max-w-[700px] flex flex-col h-full p-0">
                <SheetHeader className="p-6 pb-0">
                    <SheetTitle>
                        {step === 'select' ? 'Choose a Field Type' : `Configure ${selectedInterface?.label} Field`}
                    </SheetTitle>
                    <SheetDescription>
                        {step === 'select'
                            ? 'Select the type of field you want to create'
                            : `Set up your new ${selectedInterface?.label.toLowerCase()} field`}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {step === 'select' ? (
                        /* Interface Selection by Category */
                        <div className="space-y-8">
                            {/* Search */}
                            <Input
                                placeholder="Search fields..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="mb-4"
                            />

                            {filteredGroups.map((group) => (
                                <div key={group.key} className="space-y-3">
                                    <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">
                                        {group.name}
                                    </h3>
                                    <div className="grid grid-cols-3 gap-3">
                                        {group.interfaces.map((iface) => {
                                            const Icon = iface.icon;
                                            return (
                                                <button
                                                    key={iface.id}
                                                    onClick={() => handleSelectInterface(iface)}
                                                    className={cn(
                                                        'relative flex flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors',
                                                        iface.comingSoon
                                                            ? 'border-border opacity-50 cursor-not-allowed'
                                                            : 'border-border hover:border-primary hover:bg-accent'
                                                    )}
                                                >
                                                    {iface.comingSoon && (
                                                        <span className="absolute top-1 right-1 text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                                            Soon
                                                        </span>
                                                    )}
                                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-secondary">
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <span className="text-sm font-medium">{iface.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Field Configuration Form */
                        <div className="space-y-6">
                            {/* Interface Info Banner */}
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                                <div className="flex items-center gap-2">
                                    {selectedInterface && <selectedInterface.icon className="h-4 w-4" />}
                                    <span className="font-medium">{selectedInterface?.label}</span>
                                    <span className="text-blue-600 dark:text-blue-400">·</span>
                                    <span className="text-xs">{selectedInterface?.description}</span>
                                </div>
                            </div>

                            {/* Schema Section */}
                            <div className="space-y-4 rounded-lg border border-border p-4">
                                <h4 className="font-medium text-sm text-muted-foreground">Schema</h4>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Key Field */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-1">
                                            Key
                                            <Star className="h-3 w-3 fill-primary text-primary" />
                                        </Label>
                                        <Input
                                            value={fieldKey}
                                            onChange={(e) => {
                                                // Auto-replace spaces with underscores
                                                const value = e.target.value.replace(/ /g, '_');
                                                setFieldKey(value);
                                            }}
                                            placeholder="a_unique_column_name"
                                            className="font-mono"
                                            autoFocus
                                        />
                                        {fieldKey && !isValidKey && (
                                            <p className="text-xs text-destructive">
                                                Must start with a letter or underscore
                                            </p>
                                        )}
                                    </div>

                                    {/* Display Name Field */}
                                    <div className="space-y-2">
                                        <Label>Display Name</Label>
                                        <Input
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                            placeholder={fieldKey ? fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Custom label for this field'}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Leave empty to auto-format from key
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Type Field - Only show types supported by this interface */}
                                    {fieldType !== 'alias' && (
                                        <div className="space-y-2">
                                            <Label>Type</Label>
                                            <Select
                                                value={fieldType}
                                                onValueChange={setFieldType}
                                                disabled={typeOptions.length === 1}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select type..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {typeOptions.map((type) => (
                                                        <SelectItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Default Value (for non-alias types) */}
                                    {fieldType !== 'alias' && (
                                        <div className="space-y-2">
                                            <Label>Default Value</Label>
                                            {fieldType === 'boolean' ? (
                                                <div className="flex items-center space-x-2 h-9">
                                                    <Switch
                                                        checked={defaultValue === 'true'}
                                                        onCheckedChange={(checked) => setDefaultValue(checked ? 'true' : 'false')}
                                                    />
                                                    <span className="text-sm text-muted-foreground">
                                                        {defaultValue === 'true' ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <Input
                                                    value={defaultValue}
                                                    onChange={(e) => setDefaultValue(e.target.value)}
                                                    placeholder="NULL"
                                                    className="font-mono"
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Interface Options Section */}
                            {selectedInterface?.optionsSchema && (
                                <div className="space-y-4 rounded-lg border border-border p-4">
                                    <h4 className="font-medium text-sm text-muted-foreground">Interface Options</h4>

                                    {/* Icon for dropdown and Placeholder side by side */}
                                    {(selectedInterface.optionsSchema.icon || selectedInterface.optionsSchema.placeholder) && (
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedInterface.optionsSchema.icon && (
                                                <div className="space-y-2">
                                                    <Label>Icon</Label>
                                                    <Input
                                                        value={icon}
                                                        onChange={(e) => setIcon(e.target.value)}
                                                        placeholder="arrow_drop_down"
                                                    />
                                                </div>
                                            )}
                                            {selectedInterface.optionsSchema.placeholder && (
                                                <div className="space-y-2">
                                                    <Label>Placeholder</Label>
                                                    <Input
                                                        value={placeholder}
                                                        onChange={(e) => setPlaceholder(e.target.value)}
                                                        placeholder="Enter placeholder text..."
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Icons for input */}
                                    {(selectedInterface.optionsSchema.iconLeft || selectedInterface.optionsSchema.iconRight) && (
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedInterface.optionsSchema.iconLeft && (
                                                <div className="space-y-2">
                                                    <Label>Icon Left</Label>
                                                    <Input
                                                        value={iconLeft}
                                                        onChange={(e) => setIconLeft(e.target.value)}
                                                        placeholder="search"
                                                    />
                                                </div>
                                            )}
                                            {selectedInterface.optionsSchema.iconRight && (
                                                <div className="space-y-2">
                                                    <Label>Icon Right</Label>
                                                    <Input
                                                        value={iconRight}
                                                        onChange={(e) => setIconRight(e.target.value)}
                                                        placeholder="close"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Choices for dropdown/radio */}
                                    {selectedInterface.optionsSchema.choices && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <Label>Choices</Label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={exportChoicesToCSV}
                                                        disabled={choices.length === 0}
                                                        title="Export choices to CSV"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => document.getElementById('import-choices-csv')?.click()}
                                                        title="Import choices from CSV"
                                                    >
                                                        <Upload className="h-4 w-4" />
                                                    </Button>
                                                    <input
                                                        id="import-choices-csv"
                                                        type="file"
                                                        accept=".csv"
                                                        onChange={importChoicesFromCSV}
                                                        className="hidden"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                {choices.map((choice, index) => (
                                                    <div key={index} className="flex gap-2">
                                                        <Input
                                                            value={choice.text}
                                                            onChange={(e) => updateChoice(index, 'text', e.target.value)}
                                                            placeholder="Display text"
                                                            className="flex-1"
                                                        />
                                                        <Input
                                                            value={choice.value}
                                                            onChange={(e) => updateChoice(index, 'value', e.target.value)}
                                                            placeholder="Value"
                                                            className="flex-1 font-mono"
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeChoice(index)}
                                                            disabled={choices.length === 1}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                            <Button variant="outline" size="sm" onClick={addChoice}>
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Choice
                                            </Button>
                                        </div>
                                    )}

                                    {/* Allow Other / Allow None */}
                                    {(selectedInterface.optionsSchema.allowOther || selectedInterface.optionsSchema.allowNone) && (
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedInterface.optionsSchema.allowOther && (
                                                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                    <Label>Allow Other</Label>
                                                    <Switch checked={allowOther} onCheckedChange={setAllowOther} />
                                                </div>
                                            )}
                                            {selectedInterface.optionsSchema.allowNone && (
                                                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                    <Label>Allow None</Label>
                                                    <Switch checked={allowNone} onCheckedChange={setAllowNone} />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Markdown-specific options */}
                                    {selectedInterface.optionsSchema.toolbar && (
                                        <div className="space-y-3">
                                            <Label>Toolbar Options</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {MARKDOWN_TOOLBAR_OPTIONS.map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => toggleToolbarOption(opt.value)}
                                                        className={cn(
                                                            'px-3 py-1.5 text-sm rounded-md border transition-colors',
                                                            toolbarOptions.includes(opt.value)
                                                                ? 'bg-primary text-primary-foreground border-primary'
                                                                : 'bg-background border-border hover:bg-accent'
                                                        )}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Click to toggle toolbar buttons
                                            </p>
                                        </div>
                                    )}

                                    {/* DateTime-specific options */}
                                    {selectedInterface.optionsSchema.format && (
                                        <div className="space-y-2">
                                            <Label>Format</Label>
                                            <Select value={dateTimeFormat} onValueChange={setDateTimeFormat}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="long">Long</SelectItem>
                                                    <SelectItem value="short">Short</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Display format for date/time
                                            </p>
                                        </div>
                                    )}

                                    {/* Button Links options */}
                                    {selectedInterface.optionsSchema.links && (
                                        <div className="space-y-3">
                                            <Label>Button Links</Label>
                                            <div className="space-y-3">
                                                {links.map((link, index) => (
                                                    <div key={index} className="rounded-lg border border-border p-3 space-y-3">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-xs text-muted-foreground">Link {index + 1}</span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeLink(index)}
                                                                disabled={links.length === 1}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div className="space-y-1">
                                                                <Label className="text-xs">Label</Label>
                                                                <Input
                                                                    value={link.label}
                                                                    onChange={(e) => updateLink(index, 'label', e.target.value)}
                                                                    placeholder="Button Label"
                                                                    className="h-8 text-sm"
                                                                />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <Label className="text-xs">Icon</Label>
                                                                <Input
                                                                    value={link.icon}
                                                                    onChange={(e) => updateLink(index, 'icon', e.target.value)}
                                                                    placeholder="open_in_new"
                                                                    className="h-8 text-sm"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div className="space-y-1">
                                                                <Label className="text-xs">Type</Label>
                                                                <Select
                                                                    value={link.type}
                                                                    onValueChange={(v) => updateLink(index, 'type', v)}
                                                                >
                                                                    <SelectTrigger className="h-8 text-sm">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {BUTTON_TYPE_OPTIONS.map((opt) => (
                                                                            <SelectItem key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <Label className="text-xs flex items-center gap-1">
                                                                    URL <ExternalLink className="h-3 w-3" />
                                                                </Label>
                                                                <Input
                                                                    value={link.url}
                                                                    onChange={(e) => updateLink(index, 'url', e.target.value)}
                                                                    placeholder="https://example.com/{{id}}"
                                                                    className="h-8 text-sm font-mono"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <Button variant="outline" size="sm" onClick={addLink}>
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Link
                                            </Button>
                                            <p className="text-xs text-muted-foreground">
                                                Use {'{{field}}'} syntax for dynamic values in URLs
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* File/Image Options Section */}
                            {(selectedInterface?.id === 'file' || selectedInterface?.id === 'file-image') && (
                                <div className="space-y-4 rounded-lg border border-border p-4">
                                    <h4 className="font-medium text-sm text-muted-foreground">File Options</h4>

                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                                        <p>This field will be linked to the <span className="font-mono">cms_files</span> collection.</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Creating Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable upload button</p>
                                            </div>
                                            <Switch checked={enableCreate} onCheckedChange={setEnableCreate} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Selecting Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable select from library</p>
                                            </div>
                                            <Switch checked={enableSelect} onCheckedChange={setEnableSelect} />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Default Folder</Label>
                                        <FolderSelect
                                            value={defaultFolder}
                                            onChange={setDefaultFolder}
                                            placeholder="Choose a folder (Root for none)"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Uploaded files from this field will be saved into the selected folder.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* M2O Relational Options Section */}
                            {selectedInterface?.id === 'select-dropdown-m2o' && (
                                <div className="space-y-4 rounded-lg border border-border p-4">
                                    <h4 className="font-medium text-sm text-muted-foreground">Relationship</h4>

                                    {/* Related Collection - Required */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-1">
                                            Related Collection
                                            <Star className="h-3 w-3 fill-primary text-primary" />
                                        </Label>
                                        <Select value={relatedCollection} onValueChange={handleRelatedCollectionChange}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a collection..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {collections
                                                    .filter(c => c.collection !== collection && !c.collection.startsWith('cms_'))
                                                    .map((col) => (
                                                        <SelectItem key={col.collection} value={col.collection}>
                                                            {col.collection}
                                                        </SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            The collection this field relates to
                                            {relatedPkType && (
                                                <span className="ml-1 text-primary">
                                                    (PK type: {relatedPkType})
                                                </span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Display Template */}
                                    <div className="space-y-2">
                                        <Label>Display Template</Label>
                                        <Input
                                            value={displayTemplate}
                                            onChange={(e) => setDisplayTemplate(e.target.value)}
                                            placeholder="{{title}} - {{id}}"
                                            className="font-mono"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Template for displaying related item. Use {"{{field}}"} syntax.
                                        </p>
                                    </div>

                                    {/* Toggle options */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Creating Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable create button</p>
                                            </div>
                                            <Switch checked={enableCreate} onCheckedChange={setEnableCreate} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Selecting Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable select button</p>
                                            </div>
                                            <Switch checked={enableSelect} onCheckedChange={setEnableSelect} />
                                        </div>
                                    </div>

                                    {/* Filter - Coming Soon */}
                                    <div className="space-y-2 opacity-50">
                                        <Label>Filter</Label>
                                        <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                                            Filter configuration coming soon
                                        </div>
                                    </div>

                                    {/* Item Link */}
                                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                        <div>
                                            <Label>Item Link</Label>
                                            <p className="text-xs text-muted-foreground">Show a link to the item</p>
                                        </div>
                                        <Switch checked={enableLink} onCheckedChange={setEnableLink} />
                                    </div>
                                </div>
                            )}

                            {/* M2M Relational Options Section */}
                            {selectedInterface?.id === 'list-m2m' && (
                                <div className="space-y-4 rounded-lg border border-border p-4">
                                    <h4 className="font-medium text-sm text-muted-foreground">Many to Many Relationship</h4>

                                    {/* Relationship Diagram */}
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
                                        <div className="flex items-center justify-center gap-2 text-sm">
                                            <span className="font-mono px-2 py-1 bg-background rounded">{collection}</span>
                                            <span className="text-muted-foreground">⟷</span>
                                            <span className="font-mono px-2 py-1 bg-primary/10 text-primary rounded border border-primary/20">
                                                {junctionCollection || '(junction)'}
                                            </span>
                                            <span className="text-muted-foreground">⟷</span>
                                            <span className="font-mono px-2 py-1 bg-background rounded">
                                                {relatedCollection || '(related)'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Related Collection */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-1">
                                            Related Collection
                                            <Star className="h-3 w-3 fill-primary text-primary" />
                                        </Label>
                                        <Select value={relatedCollection} onValueChange={setRelatedCollection}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a collection..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {collections
                                                    .filter(c => c.collection !== collection && !c.collection.startsWith('cms_'))
                                                    .map((col) => (
                                                        <SelectItem key={col.collection} value={col.collection}>
                                                            {col.collection}
                                                        </SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            The collection this field will relate to
                                        </p>
                                    </div>

                                    {/* Junction Collection */}
                                    <div className="space-y-2">
                                        <Label>Junction Collection</Label>
                                        <Input
                                            value={junctionCollection}
                                            onChange={(e) => {
                                                setJunctionCollection(e.target.value.replace(/ /g, '_'));
                                                setAutoGenerateJunction(false);
                                            }}
                                            placeholder="junction_table_name"
                                            className="font-mono"
                                            disabled={autoGenerateJunction}
                                        />
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                id="auto-generate-junction"
                                                checked={autoGenerateJunction}
                                                onCheckedChange={setAutoGenerateJunction}
                                            />
                                            <Label htmlFor="auto-generate-junction" className="text-xs text-muted-foreground cursor-pointer">
                                                Auto-generate junction collection name
                                            </Label>
                                        </div>
                                    </div>

                                    {/* Display Template */}
                                    <div className="space-y-2">
                                        <Label>Display Template</Label>
                                        <Input
                                            value={displayTemplate}
                                            onChange={(e) => setDisplayTemplate(e.target.value)}
                                            placeholder="{{title}} - {{id}}"
                                            className="font-mono"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Template for displaying related items. Use {"{{field}}"} syntax.
                                        </p>
                                    </div>

                                    {/* Toggle options */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Creating Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable create button</p>
                                            </div>
                                            <Switch checked={enableCreate} onCheckedChange={setEnableCreate} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <div>
                                                <Label>Selecting Items</Label>
                                                <p className="text-xs text-muted-foreground">Enable select button</p>
                                            </div>
                                            <Switch checked={enableSelect} onCheckedChange={setEnableSelect} />
                                        </div>
                                    </div>

                                    {/* Corresponding Field */}
                                    <div className="space-y-2">
                                        <Label>Corresponding Field Name (Optional)</Label>
                                        <Input
                                            value={correspondingField}
                                            onChange={(e) => setCorrespondingField(e.target.value.replace(/ /g, '_'))}
                                            placeholder={collection}
                                            className="font-mono"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            If set, creates a field in the related collection for the reverse relationship
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions - Sticky footer */}
                <div className="flex-shrink-0 flex justify-between gap-2 border-t border-border bg-background p-4">
                    {step === 'configure' ? (
                        <>
                            <Button variant="outline" onClick={handleBack}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button onClick={handleCreate} disabled={!isValidKey || createField.isPending}>
                                    {createField.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="mr-2 h-4 w-4" />
                                            Create Field
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div />
                            <Button variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
